import { formatDate } from '@frontend/mwm-ui'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import type { Values } from '../../AssetForm'
import type { ReactElement } from 'react'
import type { QUALITY_IDENTIFIER } from '~/const/asset'
import type { IAssetFileQuality, IAssetFileWithQualifier } from '~/types/assets-management'

import AssetForm from '../../AssetForm'

import Modal from '~/components/Modal'
import { ModalHeader } from '~/components/Modal/Layout'
import MwMLoader from '~/components/MwMLoader'
import { FORM_ERRORS } from '~/const'
import { fileTypeOptions, statusOptions } from '~/const/asset'
import { assetsManagementKeys, useAssetDetailsQuery, useUpdateAssetMutation } from '~/queries/assets'
import { ASSET_STATUS } from '~/types/assets-management'
import { getStatusOptions, getPropertiesArray, getTagsArray } from '~/utils/asset'
import dayjs from '~/utils/dayjs'

const MODAL_ID = 'edit-asset-modal'

interface FormProps {
  assetId: string
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
  withAssetUploader?: boolean
}

const validationSchema = Yup.object().shape({
  fileType: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  })
    .required(FORM_ERRORS.REQUIRED)
    .nullable(),
  name: Yup.string().required(FORM_ERRORS.REQUIRED),
  properties: Yup.array()
    .of(
      Yup.object().shape({
        key: Yup.object({ label: Yup.string(), value: Yup.string() }).required(FORM_ERRORS.REQUIRED).nullable(),
        value: Yup.string().required(FORM_ERRORS.REQUIRED),
      })
    )
    .required(FORM_ERRORS.REQUIRED),
  status: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  })
    .required(FORM_ERRORS.REQUIRED)
    .nullable(),
})

const EditAssetModalForm = ({ assetId, onClose, onFailure, onSuccess, withAssetUploader }: FormProps): ReactElement => {
  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const { data: assetData, isLoading } = useAssetDetailsQuery(assetId)

  const { mutateAsync: updateAssetMutation } = useUpdateAssetMutation()

  const initialValues: Values = {
    expirationDate: assetData?.expiration_date ? formatDate(assetData.expiration_date) : '',
    fileType: fileTypeOptions.find(({ value }) => value === assetData?.file_type) || fileTypeOptions[0],
    name: assetData?.name || '',
    properties: assetData ? getPropertiesArray(assetData.tags) : [{ key: null, value: '' }],
    status: statusOptions?.find(({ value }) => value === assetData?.status) || statusOptions[0],
  }

  const formatAssetFiles = (): IAssetFileWithQualifier[] => {
    if (!assetData || !assetData.files) return []

    return assetData.files.map(({ download_url, id, mime_type, quality_identifier, ...file }) => ({
      ...file,
      file_id: id,
      objectURL: download_url,
      path: file.name,
      qualifier: quality_identifier as QUALITY_IDENTIFIER,
      type: mime_type,
    }))
  }

  const handleSubmit = async (data: Values, fileQualityIds: IAssetFileQuality[]) => {
    if (!assetData) return

    try {
      const { expirationDate, fileType, name, properties, status } = data

      await updateAssetMutation({
        expiration_date: dayjs(expirationDate).valueOf(),
        file_quality_ids: fileQualityIds,
        file_type: fileType?.value || '',
        id: assetData.id,
        name,
        status: status.value as ASSET_STATUS,
        tags: getTagsArray(properties),
      })

      queryClient.invalidateQueries({ queryKey: assetsManagementKeys.details(assetData.id) })
      onSuccess()
    } catch {
      onFailure()
    }
  }

  const handleManageAsset = () => {
    navigate(assetId)
  }

  const getWithAssetUploader = () => {
    if (assetData?.status !== ASSET_STATUS.DRAFT && (!assetData?.files || assetData?.files.length === 0)) {
      return false
    }
    return withAssetUploader
  }

  return isLoading ? (
    <MwMLoader category="item" />
  ) : (
    <AssetForm
      assetFiles={formatAssetFiles()}
      disabledFields={['fileType']}
      initialValues={initialValues}
      modalId={MODAL_ID}
      onCancel={onClose}
      onManageAssetClick={handleManageAsset}
      onSubmit={handleSubmit}
      statusOptions={getStatusOptions(assetData?.status)}
      validationSchema={validationSchema}
      withAssetUploader={getWithAssetUploader()}
      withDropzone={assetData?.status === ASSET_STATUS.DRAFT}
    />
  )
}

interface Props {
  assetId: string
  isOpen: boolean
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
  withAssetUploader?: boolean
}

const EditAssetModal = ({ assetId, isOpen, onClose, onFailure, onSuccess, withAssetUploader }: Props): ReactElement => {
  return (
    <Modal id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Update asset</ModalHeader>
      <EditAssetModalForm
        assetId={assetId}
        onClose={onClose}
        onFailure={onFailure}
        onSuccess={onSuccess}
        withAssetUploader={withAssetUploader}
      />
    </Modal>
  )
}

export default EditAssetModal
